import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm } from '@weezevent/weezjs-core';

import { BrandKit } from 'models';

import { WeezHeader, FrameCard, Button, ToggleCard, ToggleLabel, InputLabel, Input, Form, ColorPicker, ToastContext } from '@weezevent/nacre';

import css from '../templates.module.css';

const Brandkit = ({ id, organizationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [form, setForm, setFormObject] = useForm({
        id,
        name: '',
        headers: [],
        footers: [],
        enabled: true,
        primary_color: null,
        background_color: null,
        organization: organizationId,
        container_background_color: null
    });

    BrandKit.useApiModel(
        {
            organizationId,
            id,
            launch: Boolean(id),
            onSuccess: ({ object }) => setFormObject({ ...object, headers: object.headers ?? [], footers: object.footers ?? [] }, true)
        },
        [id, organizationId]
    );

    const backLink = React.useMemo(() => {
        if (!organizationId) {
            return '/emails/templates/brandkits';
        }
        return `/organizations/O${organizationId}/emails/templates/brandkits`;
    }, [organizationId]);

    const handleReset = (key, value, fallback) => {
        if (!value) {
            setForm(key, fallback);
        }
    };

    const handleSubmit = React.useCallback(() => {
        const brandKit = new BrandKit({ ...form });
        brandKit
            .save()
            .then(response => {
                if (response.status === 200) {
                    history.push(backLink);
                }
            })
            .catch(() => toast.error(t('sauron.toasts.error')));
    }, [form, backLink]);

    return (
        <>
            <WeezHeader
                rightComponent={[<Button key="save" primary inverted label={t('common.cta.save')} onClick={handleSubmit} disabled={false} />]}
                title={id ? form.name || ' ' : t('sauron.emails.templates.brandkits.new')}
                backLink={{
                    onClick: () => {
                        history.push(backLink);
                    }
                }}
            />
            <FrameCard title={t('sauron.emails.templates.brandkits.label_sing')} separator>
                <ToggleLabel
                    inlinePosition="right"
                    label={t('sauron.emails.templates.brandkits.enable')}
                    checked={form.enabled}
                    onChange={({ target: { checked } }) => setForm('enabled', checked)}
                />

                <Form.Field>
                    <InputLabel label={t('sauron.emails.templates.brandkits.name')} />
                    <Input placeholder={t('sauron.emails.templates.brandkits.name')} value={form.name} onChange={({ target: { value } }) => setForm('name', value)} />
                </Form.Field>

                <ToggleCard
                    className={css['color-picker-card']}
                    subtitle={t('sauron.emails.templates.brandkits.toggles.primary_color_subtitle')}
                    title={t('sauron.emails.templates.brandkits.toggles.primary_color')}
                    toggle={Boolean(form.primary_color)}
                    onToggle={value => handleReset('primary_color', value, null)}
                >
                    <ColorPicker
                        label={t('sauron.emails.templates.brandkits.toggles.primary_color')}
                        onChange={(_, value) => setForm('primary_color', value)}
                        value={form.primary_color ?? ''}
                    />
                    <span className={css['color-picker-label']}>{t('sauron.emails.templates.brandkits.toggles.primary_color')}</span>
                </ToggleCard>

                <ToggleCard
                    className={css['color-picker-card']}
                    subtitle={t('sauron.emails.templates.brandkits.toggles.background_color_subtitle')}
                    title={t('sauron.emails.templates.brandkits.toggles.background_color')}
                    toggle={Boolean(form.background_color)}
                    onToggle={(_, value) => handleReset('background_color', value, null)}
                >
                    <ColorPicker onChange={(_, value) => setForm('background_color', value)} value={form.background_color ?? ''} />
                    <span className={css['color-picker-label']}>{t('sauron.emails.templates.brandkits.toggles.background_color')}</span>
                </ToggleCard>

                <ToggleCard
                    className={css['color-picker-card']}
                    subtitle={t('sauron.emails.templates.brandkits.toggles.container_background_color_subtitle')}
                    title={t('sauron.emails.templates.brandkits.toggles.container_background_color')}
                    toggle={Boolean(form.container_background_color)}
                    onToggle={value => handleReset('container_background_color', value, null)}
                >
                    <ColorPicker onChange={(_, value) => setForm('container_background_color', value)} value={form.container_background_color ?? ''} />
                    <span className={css['color-picker-label']}>{t('sauron.emails.templates.brandkits.toggles.container_background_color')}</span>
                </ToggleCard>

                <ToggleCard
                    disabled
                    subtitle={t('sauron.emails.templates.brandkits.toggles.header_subtitle')}
                    title={t('sauron.emails.templates.brandkits.toggles.header')}
                    toggle={!!form.headers.length}
                    onToggle={value => handleReset('headers', value, [])}
                >
                    {/* todo unlayer editor */}
                </ToggleCard>

                <ToggleCard
                    disabled
                    subtitle={t('sauron.emails.templates.brandkits.toggles.footer_subtitle')}
                    title={t('sauron.emails.templates.brandkits.toggles.footer')}
                    toggle={!!form.footers.length}
                    onToggle={value => handleReset('footers', value, [])}
                >
                    {/* todo unlayer editor */}
                </ToggleCard>
            </FrameCard>
        </>
    );
};

export default Brandkit;
